import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Users, UserPlus, Bike, Clock } from "lucide-react";
import api from "../lib/api";

export default function RsvpListDialog({ open, onOpenChange, rideId, rideName }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !rideId) return;
    setLoading(true);
    api
      .get(`/rides/${rideId}/rsvp-public-list`)
      .then((res) => setList(res.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [open, rideId]);

  const confirmed = list.filter((r) => !r.waitlist_position);
  const waitlist = list
    .filter((r) => (r.waitlist_position || 0) >= 1)
    .sort((a, b) => (a.waitlist_position || 0) - (b.waitlist_position || 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-[#12141D] border-white/10 max-h-[85vh] overflow-y-auto"
        data-testid="rsvp-list-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Users size={18} style={{ color: "#00E5FF" }} />
            <span className="label-caps" style={{ color: "#00E5FF" }}>
              Lista de Presença
            </span>
          </div>
          <DialogTitle className="font-display text-2xl sm:text-3xl uppercase text-white">
            {rideName || "Confirmados"}
          </DialogTitle>
          <div className="text-white/60 text-sm">
            {loading
              ? "Carregando confirmados..."
              : `${confirmed.length} ${confirmed.length === 1 ? "piloto confirmado" : "pilotos confirmados"}${waitlist.length > 0 ? ` · ${waitlist.length} na fila` : ""}`}
          </div>
        </DialogHeader>

        {!loading && list.length === 0 && (
          <div
            className="label-caps text-white/60 text-center py-6"
            data-testid="rsvp-list-empty"
          >
            Lista não liberada para esse rolê!
          </div>
        )}

        {confirmed.length > 0 && (
          <div className="space-y-2 mt-2" data-testid="rsvp-list-items-confirmed">
            {confirmed.map((r, idx) => (
              <div
                key={`c-${idx}`}
                className="flex items-center justify-between gap-3 border border-white/10 bg-[#0B0D14] px-4 py-3"
                data-testid={`rsvp-list-item-${idx}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-body text-sm sm:text-base truncate">
                      {r.rider_name}
                    </span>
                    {r.is_guest && (
                      <span
                        className="label-caps px-1.5 py-0.5 border text-[10px] shrink-0"
                        style={{
                          color: "#F59E0B",
                          borderColor: "rgba(245,158,11,0.35)",
                        }}
                      >
                        Convidado
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/60">
                    <span className="inline-flex items-center gap-1">
                      <Bike size={12} /> {r.motorcycle_model}
                    </span>
                    {r.companion ? (
                      <span className="inline-flex items-center gap-1">
                        <UserPlus size={12} /> {r.companion}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div
                  className="font-display text-lg shrink-0"
                  style={{ color: "#00E5FF" }}
                >
                  #{idx + 1}
                </div>
              </div>
            ))}
          </div>
        )}

        {waitlist.length > 0 && (
          <div className="mt-6" data-testid="rsvp-list-waitlist-section">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={14} style={{ color: "#F59E0B" }} />
              <span
                className="label-caps"
                style={{ color: "#F59E0B", letterSpacing: "0.3em" }}
              >
                Fila de Espera
              </span>
            </div>
            <div className="space-y-2">
              {waitlist.map((r, idx) => (
                <div
                  key={`w-${idx}`}
                  className="flex items-center justify-between gap-3 border px-4 py-3"
                  style={{
                    borderColor: "rgba(245,158,11,0.35)",
                    background: "rgba(245,158,11,0.04)",
                  }}
                  data-testid={`rsvp-list-waitlist-item-${idx}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-body text-sm sm:text-base truncate">
                        {r.rider_name}
                      </span>
                      {r.is_guest && (
                        <span
                          className="label-caps px-1.5 py-0.5 border text-[10px] shrink-0"
                          style={{
                            color: "#F59E0B",
                            borderColor: "rgba(245,158,11,0.35)",
                          }}
                        >
                          Convidado
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/60">
                      <span className="inline-flex items-center gap-1">
                        <Bike size={12} /> {r.motorcycle_model}
                      </span>
                      {r.companion ? (
                        <span className="inline-flex items-center gap-1">
                          <UserPlus size={12} /> {r.companion}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div
                    className="font-display text-lg shrink-0"
                    style={{ color: "#F59E0B" }}
                  >
                    #{r.waitlist_position}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
